// src/hooks/useIcon.tsx
import React, { useMemo, useEffect } from 'react';
import { useModelStore } from 'src/stores/modelStore';
import { getItemByIdOrThrow } from 'src/utils';
import { IsometricIcon } from 'src/components/SceneLayers/Nodes/Node/IconTypes/IsometricIcon';
import { NonIsometricIcon } from 'src/components/SceneLayers/Nodes/Node/IconTypes/NonIsometricIcon';
import { LucideIcon } from 'src/components/SceneLayers/Nodes/Node/IconTypes/LucideIcon';
import { DEFAULT_ICON } from 'src/config';
import { iconPackManager } from '@features/components/iconPacks';

/**
 * useIcon hook - Gets icon definition and renders appropriate component
 *
 * @param iconId - The icon ID to look up
 * @param nodeId - The model item ID (required for color support)
 * @returns Icon data and rendered component
 */
export const useIcon = (iconId: string | undefined, nodeId: string) => {
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const icons = useModelStore((state) => state.icons);

  const icon = useMemo(() => {
    if (!iconId) return DEFAULT_ICON;

    // First try to get from model store (legacy icons)
    try {
      return getItemByIdOrThrow(icons, iconId).value;
    } catch {
      // If not in model store, check IconPackManager
      const iconDef = iconPackManager.getIconById(iconId);
      if (iconDef) {
        // Handle different icon sources
        if (
          iconDef.source === 'reshot' ||
          iconDef.source === 'cloud-provider' ||
          iconDef.source === 'custom'
        ) {
          // For local SVG files (Reshot, cloud providers, custom)
          return {
            id: iconDef.id,
            name: iconDef.name,
            url: iconDef.svgPath || '', // Use svgPath as URL
            collection: iconDef.pack,
            isIsometric: iconDef.isIsometric,
            source: iconDef.source
          };
        } else {
          // For Lucide icons (or icons without explicit source)
          return {
            id: iconDef.id,
            name: iconDef.name,
            url: '', // No URL for Lucide icons
            collection: iconDef.pack,
            isIsometric: iconDef.isIsometric,
            lucideIcon: iconDef.icon, // Store the Lucide component
            source: iconDef.source || 'lucide'
          };
        }
      }
      return DEFAULT_ICON;
    }
  }, [icons, iconId]);

  useEffect(() => {
    // Set loaded immediately for Lucide and local SVG icons
    if (
      'lucideIcon' in icon ||
      icon.source === 'reshot' ||
      icon.source === 'cloud-provider' ||
      icon.source === 'custom'
    ) {
      setHasLoaded(true);
    }
  }, [icon]);

  const iconComponent = useMemo(() => {
    // Handle Lucide icons
    if ('lucideIcon' in icon && icon.lucideIcon) {
      return <LucideIcon IconComponent={icon.lucideIcon} nodeId={nodeId} />;
    }

    // 2. ✅ CHECK ISOMETRIC FIRST - This is the key fix!
    if (icon.isIsometric) {
      return (
        <IsometricIcon
          url={icon.url}
          onImageLoaded={() => {
            setHasLoaded(true);
          }}
        />
      );
    }

    // 3. Non-isometric icons (flat images that need projection)
    setHasLoaded(true);
    return <NonIsometricIcon icon={icon} />;
  }, [icon, nodeId]);

  return {
    icon,
    iconComponent,
    hasLoaded
  };
};
